import { useReducer, useMemo, useEffect, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { v1 as uuidv1 } from 'uuid';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useLoadScriptStatus from '#client/hooks/useLoadScript';
import { buildAddress } from '#client/utils/google-maps';

import { CustomPaper } from './components/CustomPaper';

import {
  UPDATE,
  START_ASYNC,
  END_ASYNC,
  RESET,
  emptyAddress,
  RESET_ADDRESS,
  FOCUS,
  BLUR,
  DEFAULT_ADDRESS_OPTION,
  DIFFERENT_ADDRESS_OPTION,
  MANUAL_ADDRESS_OPTION
} from './constants/address';
import { ADDRESS_INVALID_MESSAGE, ZIP_CODE_INVALID_MESSAGE } from './constants/errorMessages';
import { STATES_LIST } from './constants/states';
import { regexZipCode } from './utils/regex';

const reducer = (state, action) => {
  switch (action.type) {
    case UPDATE:
      return {
        ...state,
        ...action.payload
      };
    case START_ASYNC:
      return {
        ...state,
        isLoading: true
      };
    case END_ASYNC:
      return {
        ...state,
        isLoading: false
      };
    case RESET:
      return {
        ...state,
        ...emptyAddress
      };
    case RESET_ADDRESS:
      return {
        ...state,
        ...emptyAddress,
        addressLine2: state.addressLine2,
        valid: false
      };
    case FOCUS:
      return {
        ...state,
        isDirty: true,
        isFocus: true
      };
    case BLUR:
      return {
        ...state,
        isFocus: false
      };

    default:
      return state;
  }
};

export const AddressUIField = ({ field, data, dataChangeHandler = () => {}, disabled }) => {
  const defaultAddress = useMemo(() => {
    const { addressLine1, addressLine2, city, state, country, zip } = data;
    return {
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      zip,
      valid: true
    };
    // Expect it keep first data value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  const [address, dispatchAddress] = useReducer(reducer, {
    ...emptyAddress,
    pick: DEFAULT_ADDRESS_OPTION,
    valid: false,
    isDirty: false,
    isFocus: false,
    isLoading: false
  });

  const [selectedState, setSelectedState] = useState(null);
  const [isValidZipCode, setIsValidZipCode] = useState(true);

  const isShowError = address.isDirty && !address.isLoading && !address.isFocus && !address.valid;

  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const { init, ready, suggestions, value, setValue, clearSuggestions } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' }
    },
    debounce: 200,
    initOnMount: false
  });
  const { ZENBUSINESS_GOOGLE_MAPS_KEY } = useEnv();
  const googleMapScriptStatus = useLoadScriptStatus(
    `https://maps.googleapis.com/maps/api/js?key=${ZENBUSINESS_GOOGLE_MAPS_KEY}&libraries=places`
  );

  useEffect(() => {
    // Start usePlacesAutocomplete hook
    if (googleMapScriptStatus === 'ready' && !ready) {
      init();
    }
  }, [googleMapScriptStatus, init, ready]);

  const disableAddressField = disabled || !ready || googleMapScriptStatus !== 'ready';
  const isShowOptions = address.isFocus && suggestions?.status === 'OK';
  const trackAddressFieldClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    elementText: 'enter_new_address_textbox',
    appPageName: 'banking_application'
  });

  const onDataChanged = (newAddress) => {
    const { addressLine1, addressLine2, city, state, country, zip, valid } = newAddress;
    if (valid) {
      field.setLoading();
      if (newAddress.pick !== DEFAULT_ADDRESS_OPTION) {
        /**
         * Tracking Details: Tracking to be added to the banking application Collect Info screen.
         * Question:
         *          Where are customers typically dropping off on this screen?
         *          Do they typically drop off at SSN?
         *          How often are they viewing the user agreements?
         */
        trackAddressFieldClick();
      }
    }

    dataChangeHandler(
      valid,
      {
        bankAccountMembers: [
          {
            addressLine1,
            addressLine2,
            city,
            state,
            country,
            zip
          }
        ]
      },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError, valid);
        forceUpdate();
        dispatchAddress({ type: END_ASYNC });
      }
    );
  };

  const handleUnSave = () => {
    dataChangeHandler(false, null, (forceUpdate, { isSave, isError }) => {
      field.setSaved(isSave, isError, false);
      forceUpdate();
    });
  };

  const trackAddressFieldChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    elementText:
      address.pick === DIFFERENT_ADDRESS_OPTION ? 'imported_address' : 'enter_new_address_checkbox',
    appPageName: 'banking_application'
  });
  const handlePickChange = (event) => {
    const option = event.target.value;
    const newAddress = {
      pick: option,
      isDirty: false,
      ...(option === DEFAULT_ADDRESS_OPTION && { ...defaultAddress, valid: true }),
      ...(option !== DEFAULT_ADDRESS_OPTION && { ...emptyAddress, valid: false })
    };
    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
    /**
     * Tracking Details: Tracking to be added to the banking application Collect Info screen.
     * Question:
     *          Where are customers typically dropping off on this screen?
     *          Do they typically drop off at SSN?
     *          How often are they viewing the user agreements?
     */
    trackAddressFieldChangeClick();
  };

  const handleGoogleAddressChange = async (_, option) => {
    const { description, place_id: placeId } = option || {};
    if (!placeId) {
      dispatchAddress({ type: RESET });
      return;
    }
    dispatchAddress({ type: START_ASYNC });
    const googleAddress = await buildAddress(description, placeId);
    const valid = field.validateAddressLine1(googleAddress);
    const { addressLine1, city, state, country, zip } = googleAddress;

    const newAddress = {
      addressLine1,
      addressLine2: address.addressLine2,
      city,
      state,
      country,
      zip,
      valid
    };

    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
  };

  const handleBlurOrClickAway = () => {
    dispatchAddress({ type: BLUR });
    clearSuggestions();
    setValue('', false);
  };

  const handleChangeAddressLine2 = (event) => {
    const addressLine2 = event.target.value;
    const newAddress = {
      ...address,
      addressLine2
    };
    dispatchAddress({ type: UPDATE, payload: newAddress });
  };

  const onBlurAddressLine2 = (event) => {
    const addressLine2 = event.target.value;
    const newAddress = {
      ...address,
      addressLine2
    };
    newAddress.valid = field.validateAddressLine1(newAddress);

    handleBlurOrClickAway();
    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
  };

  const handleChangeInput = (event, fieldName) => {
    const fieldValue = event.target.value;

    const newAddress = {
      ...address,
      [fieldName]: fieldValue
    };
    dispatchAddress({ type: UPDATE, payload: newAddress });
  };

  const handleZipChange = (event) => {
    const inputValue = event.target.value;

    const numbersOnly = inputValue.replace(/\D/g, '');
    let formattedValue = '';

    formattedValue += numbersOnly.slice(0, 5);
    if (numbersOnly.length > 5) {
      formattedValue += `-${numbersOnly.slice(5, 9)}`;
    }

    const newAddress = {
      ...address,
      zip: formattedValue
    };

    dispatchAddress({ type: UPDATE, payload: newAddress });
  };

  const handleChangeSelect = (_, newValue, fieldName) => {
    const newAddress = {
      ...address,
      [fieldName]: newValue?.key || '',
      country: 'US'
    };

    newAddress.valid = field.validateAddressLine1(newAddress);

    setSelectedState(newValue);
    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
  };

  const onBlurInput = (event, fieldName) => {
    const fieldValue = event.target.value;
    const newAddress = {
      ...address,
      [fieldName]: fieldValue
    };
    const isNewAddressValid = field.validateAddressLine1(newAddress);
    newAddress.valid = isNewAddressValid;

    if (fieldName === 'zip') {
      const validZipCode = regexZipCode.test(fieldValue);
      newAddress.valid = isNewAddressValid && validZipCode;

      setIsValidZipCode(validZipCode);
    }

    handleBlurOrClickAway();
    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
  };

  const onBlurSelectInput = () => {
    handleBlurOrClickAway();
  };

  const handleFocus = () => {
    dispatchAddress({ type: FOCUS });
  };

  const handleClear = () => {
    const newAddress = {
      ...emptyAddress,
      valid: false
    };

    dispatchAddress({ type: UPDATE, payload: newAddress });
    onDataChanged(newAddress);
  };

  const handleOtherReason = (event, newInputValue, reason) => {
    switch (reason) {
      case 'clear':
        handleClear();
        break;
      case 'input':
        if (value !== newInputValue) {
          clearSuggestions();
          setValue(newInputValue, !!newInputValue);
          dispatchAddress({ type: RESET_ADDRESS });
          handleUnSave();
        }
        break;
      default:
        break;
    }
  };

  const renderOption = (props, option) => (
    <Text
      {...props}
      component="span"
      id={option?.place_id}
      key={option?.place_id}
    >
      <Text component="div">
        <Text component="span">{option?.description}</Text>
      </Text>
    </Text>
  );

  return (
    <Stack spacing="16px">
      <FormControl disabled={disableAddressField}>
        <RadioGroup
          onChange={handlePickChange}
          sx={{ gap: '24px' }}
          value={address.pick}
        >
          {field.hasValue(defaultAddress) && (
            <FormControlLabel
              control={<Radio />}
              label={<Text size="xl">{field.getValue(defaultAddress)}</Text>}
              value={DEFAULT_ADDRESS_OPTION}
            />
          )}
          <FormControlLabel
            control={<Radio />}
            label={<Text size="xl">A different address</Text>}
            value={DIFFERENT_ADDRESS_OPTION}
          />
          {address.pick === DIFFERENT_ADDRESS_OPTION && (
            <Stack paddingLeft="32px">
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing="16px"
              >
                <Box width={isMobile ? '100%' : '70%'}>
                  <AutoCompleteInput
                    autoHighlight
                    blurOnSelect
                    disabled={disableAddressField}
                    freeSolo
                    getOptionLabel={(option) => option?.description || ''}
                    onChange={handleGoogleAddressChange}
                    onInputChange={handleOtherReason}
                    open={isShowOptions}
                    openOnFocus
                    options={suggestions?.data}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={isShowError}
                        InputProps={{
                          ...params.InputProps
                        }}
                        label="Address"
                        onBlur={handleBlurOrClickAway}
                        onFocus={handleFocus}
                      />
                    )}
                    renderOption={renderOption}
                  />
                </Box>
                <Box width={isMobile ? '100%' : '30%'}>
                  <TextField
                    disabled={disableAddressField}
                    id="addressLine2"
                    label="Unit/Apt/Suite"
                    onBlur={onBlurAddressLine2}
                    onChange={handleChangeAddressLine2}
                    type="text"
                    value={address.addressLine2}
                  />
                </Box>
              </Stack>
              {isShowError && (
                <Text
                  color="red"
                  component="div"
                  pt="8px"
                  size="xs"
                >
                  {ADDRESS_INVALID_MESSAGE}
                </Text>
              )}
            </Stack>
          )}
          <FormControlLabel
            control={<Radio />}
            label={<Text size="xl">Manual address</Text>}
            value={MANUAL_ADDRESS_OPTION}
          />
        </RadioGroup>
      </FormControl>
      {address.pick === MANUAL_ADDRESS_OPTION && (
        <Stack
          paddingLeft="32px"
          spacing="16px"
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing="16px"
          >
            <Box width={isMobile ? '100%' : '70%'}>
              <TextField
                disabled={disableAddressField}
                id="addressLine1"
                inputProps={{ maxLength: 50 }}
                label="Address"
                onBlur={(event) => onBlurInput(event, 'addressLine1')}
                onChange={(event) => handleChangeInput(event, 'addressLine1')}
                type="text"
                value={address.addressLine1}
              />
            </Box>
            <Box width={isMobile ? '100%' : '30%'}>
              <TextField
                disabled={disableAddressField}
                id="addressLine2"
                inputProps={{ maxLength: 20 }}
                label="Unit/Apt/Suite"
                onBlur={(event) => onBlurInput(event, 'addressLine2')}
                onChange={(event) => handleChangeInput(event, 'addressLine2')}
                type="text"
                value={address.addressLine2}
              />
            </Box>
          </Stack>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing="16px"
          >
            <Box width="100%">
              <TextField
                disabled={disableAddressField}
                id="city"
                inputProps={{ maxLength: 50 }}
                label="City"
                onBlur={(event) => onBlurInput(event, 'city')}
                onChange={(event) => handleChangeInput(event, 'city')}
                type="text"
                value={address.city}
              />
            </Box>
          </Stack>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing="16px"
          >
            <Box width={isMobile ? '100%' : '50%'}>
              <AutoCompleteInput
                blurOnSelect
                disabled={disabled}
                getOptionLabel={(option) => option.value || ''}
                onChange={(event, newValue) => handleChangeSelect(event, newValue, 'state')}
                openOnFocus
                options={STATES_LIST}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      // Use for preventing auto suggestion from browser
                      name: uuidv1()
                    }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    InputProps={{
                      ...params.InputProps
                    }}
                    label="State"
                    onBlur={(event) => onBlurSelectInput(event, 'state')}
                  />
                )}
                value={selectedState || null}
              />
            </Box>
            <Box width={isMobile ? '100%' : '50%'}>
              <TextField
                disabled={disableAddressField}
                id="zip"
                inputProps={{ maxLength: 10 }}
                label="Zip"
                onBlur={(event) => onBlurInput(event, 'zip')}
                onChange={handleZipChange}
                type="text"
                value={address.zip}
              />
              {!isValidZipCode && (
                <Text
                  color="red"
                  component="div"
                  pt="8px"
                  size="xs"
                >
                  {ZIP_CODE_INVALID_MESSAGE}
                </Text>
              )}
            </Box>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

AddressUIField.propTypes = {
  data: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    zip: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    getValue: PropTypes.func,
    hasValue: PropTypes.func,
    validateAddressLine1: PropTypes.func,
    validateAddressLine2: PropTypes.func
  }),
  disabled: PropTypes.bool
};
